<template>
    <Head title="Viewing Review"/>

    <BreezeAuthenticatedLayout>
        <template #header>
            <div class="flex items-center gap-4">
                <h2 class="flex-grow text-xl font-semibold leading-tight text-gray-800">
                    Editing review by {{ review.user.name }} on {{ review.product.name }}
                </h2>

                <p class="text-sm text-placeholder">#{{ review.id }}</p>
            </div>
        </template>

        <BreadCrumbs :items="navItems"></BreadCrumbs>

        <form @submit.prevent="editReview.put(route('admin.reviews.update', review))">
            <div class="grid grid-cols-1 xl:grid-cols-5 gap-4">
                <div class="xl:col-span-3">
                    <AppCard>
                        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                            <div class="md:col-span-2">
                                <AdminAppLabel>Title</AdminAppLabel>
                                <InputBasic v-model="editReview.title" :error="errors['title']"></InputBasic>
                                <input-error :error="errors['title']"></input-error>
                            </div>

                            <div class="md:pt-6">
                                <InputCheckbox v-model="editReview.approved">
                                    Approved review?
                                </InputCheckbox>
                            </div>
                        </div>

                        <div class="mt-4">
                            <AdminAppLabel>Comment</AdminAppLabel>
                            <InputWysiwyg v-model="editReview.comment" :error="errors['comment']"></InputWysiwyg>
                            <input-error :error="errors['comment']"></input-error>
                        </div>

                        <div class="mt-4">
                            <admin-app-label>Reviewed Product</admin-app-label>
                            <ProductSelection v-model="editReview.product"
                                              :error="errors['product_id']"
                            ></ProductSelection>
                            <input-error :error="errors['product_id']"></input-error>
                        </div>

                        <div class="grid grid-col-1 md:grid-cols-3 items-center mt-4 gap-4">
                            <div>
                                <InputCheckbox v-model="editReview.verified">
                                    Verified Purchase?
                                </InputCheckbox>
                            </div>

                            <div :class="{'invisible': !editReview.verified}">
                                <AdminAppLabel>Order Reference</AdminAppLabel>
                                <InputBasic v-model="editReview.order_reference"
                                            :error="errors['order_reference']"></InputBasic>
                            </div>

                            <div class="md:pt-4" v-if="editReview.verified">
                                <input-error :error="errors['order_reference']"></input-error>
                            </div>
                        </div>
                    </AppCard>

                    <div class="flex flex-col md:flex-row items-center mt-4">
                        <admin-link as="button" type="button" class="bg-red mr-auto" @click="showDeleteModal = true">
                            Delete
                        </admin-link>

                        <admin-delete-modal v-if="showDeleteModal"
                                            msg="Are you sure you want to delete this review?"
                                            type="review"
                                            @toggle-delete-modal="showDeleteModal = false"
                                            @delete-item="$inertia.delete(route('admin.reviews.destroy', review))"></admin-delete-modal>

                        <admin-link type="submit" class="bg-primary">
                            Update
                        </admin-link>
                    </div>
                </div>

                <div class="xl:col-span-2">
                    <ReviewCard :review="editReview"></ReviewCard>

                    <p v-if="!review.approved" class="text-placeholder text-sm">
                        This review has not been approved, and will not appear anywhere on the site.
                    </p>
                </div>
            </div>

        </form>
    </BreezeAuthenticatedLayout>
</template>

<script setup>
import BreezeAuthenticatedLayout from "@/Layouts/Admin/Authenticated.vue";
import BreadCrumbs from "@/Components/Admin/BreadCrumbs";
import ReviewCard from "@/Components/Reviews/ReviewCard";
import InputBasic from "@/Components/Admin/Forms/Inputs/InputBasic";
import AdminAppLabel from "@/Components/Admin/Forms/AdminAppLabel";
import InputWysiwyg from "@/Components/Admin/Forms/Inputs/InputWysiwyg";
import AppCard from "@/Components/AppCard";
import ProductSelection from "@/Components/Admin/Forms/ProductSelection";
import {Head, useForm} from "@inertiajs/inertia-vue3";
import InputCheckbox from "@/Components/Forms/Inputs/InputCheckbox";
import AdminLink from "@/Components/Admin/AdminLink";
import {watch, ref} from "vue";
import InputError from "@/Components/Forms/Inputs/InputError";
import AdminDeleteModal from "@/Components/Admin/AdminDeleteModal";

const props = defineProps({
    review: Object,
    errors: Object
});

const editReview = useForm({...props.review});

// Update product ID if product has been changed.
watch(() => editReview.product, newProduct => {
    editReview.product_id = newProduct.id;
});

const navItems = [
    {title: "Reviews", path: route("admin.reviews.index")},
    {title: `#${props.review.id}`, path: route("admin.reviews.edit", props.review)},
];

const showDeleteModal = ref(false);
</script>

<style scoped>

</style>
